import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentService } from '@app/shared/services';
import { Subscription } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastrService } from 'ngx-toastr';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ChangePinDialogComponent } from '../change-pin-dialog/change-pin-dialog.component';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'create-share-link',
  templateUrl: './create-share-link.component.html',
  styleUrls: ['./create-share-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateShareLinkComponent implements OnInit, OnDestroy {
  shareLink;
  sharePin;
  usePin;
  stateObject;
  vaultStateObject$: any;
  shareEamilForm: UntypedFormGroup;
  subscription: Subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<CreateShareLinkComponent>,
    private dialog: MatDialog,
    private documentService: DocumentService,
    public clipboard: Clipboard,
    private toastr: ToastrService,
    private _fb: UntypedFormBuilder,
    private vaultService: VaultStoreEffects,
    private changeDetectionRef: ChangeDetectorRef
  ) {
    this.vaultStateObject$ = this.vaultService.vaultObject;
    this.subscription.add(
      this.vaultStateObject$.subscribe((item) => {
        this.stateObject = item;
        this.changeDetectionRef.markForCheck();
      })
    );
    this.getFormShareLink();
  }

  ngOnInit(): void {
    this.shareEamilForm = this._fb.group({
      email: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,25}$')]],
    });
  }

  getFormShareLink() {
    this.subscription.add(
      this.documentService.getFormShareLink(this.stateObject.userId, this.data.vaultFormId).subscribe(
        (data) => {
          this.shareLink = data.files.link;
          this.sharePin = data.files.pin;
          this.usePin = data.files.use_pin;
          this.changeDetectionRef.detectChanges();
        },
        (error: any) => {
          this.toastr.error(error);
        }
      )
    );
  }

  copyLink(value) {
    this.clipboard.copy(value);
    this.toastr.success('Link copied');
  }

  sendShareLinkEmail() {
    if (!this.shareEamilForm.valid) {
      this.shareEamilForm.controls['email'].setErrors({});
      this.changeDetectionRef.detectChanges();
      return;
    }
    const payload = {
      vault_user_form_id: this.data.vaultFormId,
      user_id: this.stateObject.userId,
      email: this.shareEamilForm.controls['email'].value,
    };
    this.subscription.add(
      this.documentService.sendShareLinkEmail(payload).subscribe(
        (data) => {
          this.toastr.success(`Link sent to ${this.shareEamilForm.controls['email'].value}`);
          this.shareEamilForm.controls['email'].setValue('');
          this.shareEamilForm.controls['email'].setErrors(null);
          this.changeDetectionRef.detectChanges();
        },
        (error: any) => {
          this.toastr.error(error);
        }
      )
    );
  }

  openChangePinDialog(pin) {
    const dialogRef = this.dialog.open(ChangePinDialogComponent, {
      width: '403px',
      height: '230px',
      panelClass: 'create-share-link-dialog',
      data: { pin: pin, vaultFormId: this.data.vaultFormId, title: 'Change PIN', openedFrom: 'shareLink' },
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((data) => {
        this.getFormShareLink();
      })
    );
  }

  async onCheckChange(event) {
    const payload = {
      vault_user_form_id: this.data.vaultFormId,
      user_id: this.stateObject.userId,
      use_pin: event.checked,
    };
    this.subscription.add(
      this.documentService.updateUsePin(payload).subscribe(
        (data) => {
          this.usePin = data.use_pin;
          !this.usePin
            ? this.toastr.success('Turned off ‘Require a PIN to access file’ ')
            : this.toastr.success('Turned on ‘Require a PIN to access file’ ');
        },
        (error: any) => {
          this.toastr.error(error);
        }
      )
    );
    this.changeDetectionRef.detectChanges();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
